import { type ReactNode, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  IMPORT_COLUMNS,
  IMPORT_COLUMN_ALIASES,
  type ImportColumn,
  type ImportRunResponse,
} from '@aps/contracts';
import { Button, DataTable, type DataTableColumn, ImageDropzone, formatMoney } from '@aps/ui';
import { ApiError, api } from '../lib/api.js';
import { messages } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';

/**
 * Excel/CSV importi — IKKI BOSQICH.
 *
 * Fayl yuklangach TEKSHIRUV natijasi ko'rsatiladi va hech narsa
 * yozilmaydi. Odam nima yaratilishini, nima yangilanishini va qaysi
 * qatorlar tushib qolishini KO'RIB TURIB qo'llaydi. Bir bosqichli
 * import 300 ta tovarni bir bosishda buzib qo'yardi.
 */
export function ImportScreen(): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();

  const [run, setRun] = useState<ImportRunResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onError = (cause: unknown): void => {
    setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
  };

  const validate = useMutation({
    mutationFn: (file: File) => api.validateImport(file),
    onSuccess: (result) => {
      setRun(result);
      setError(null);
    },
    onError: (cause: unknown) => {
      setRun(null);
      onError(cause);
    },
  });

  const apply = useMutation({
    mutationFn: (runId: string) => api.applyImport(runId),
    onSuccess: (result) => {
      // Ustunlar moslashuvi FAYLDAN kelib chiqadi va yozuvda
      // saqlanmaydi, shuning uchun `apply` javobida ular bo'sh
      // qaytadi. Oldingi holatdan olib qo'yiladi — aks holda
      // qo'llagandan keyin "Tanilgan ustunlar" qatori `—` bo'lib
      // qolardi va odam nima o'qilganini ko'ra olmasdi.
      setRun((current) => ({
        ...result,
        columns: current?.columns ?? result.columns,
        ignoredColumns: current?.ignoredColumns ?? result.ignoredColumns,
        stockShops: current?.stockShops ?? result.stockShops,
      }));
      setError(null);
      void client.invalidateQueries({ queryKey: ['admin-products'] });
    },
    onError,
  });

  return (
    <section className="adm-page">
      <header className="adm-page__header">
        <h1 className="adm-page__title">{t.navImport}</h1>
      </header>

      <section className="adm-panel">
        <h2 className="adm-panel__title">{t.importFile}</h2>
        <div className="adm-panel__body">
          <ImageDropzone
            label={t.importUpload}
            hint={t.importUploadHint}
            accept=".xlsx,.csv"
            disabled={validate.isPending}
            onFiles={(files) => {
              const file = files[0];
              if (file !== undefined) validate.mutate(file);
            }}
          />
          <ColumnHelp />
        </div>
      </section>

      {error !== null && (
        <p className="aps-error" role="alert">
          {error}
        </p>
      )}

      {validate.isPending && <p className="aps-muted">{t.loading}</p>}

      {run !== null && (
        <ImportResult
          run={run}
          applying={apply.isPending}
          onApply={() => {
            apply.mutate(run.id);
          }}
        />
      )}
    </section>
  );
}

/**
 * Qaysi sarlavhalar tanilishini KO'RSATADI.
 *
 * Usiz odam faylni yuklaydi, "sku topilmadi" xatosini oladi va nima
 * yozish kerakligini taxmin qilishga majbur bo'ladi.
 */
function ColumnHelp(): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          setOpen(true);
        }}
      >
        {t.importColumnsShow}
      </Button>
    );
  }

  return (
    <div className="adm-import__help">
      <p className="aps-field__hint">{t.importColumnsHint}</p>
      <ul className="adm-import__columns">
        {IMPORT_COLUMNS.map((column: ImportColumn) => (
          <li key={column}>
            <code>{column}</code>
            <span className="aps-muted"> — {IMPORT_COLUMN_ALIASES[column].join(', ')}</span>
          </li>
        ))}
      </ul>
      <p className="aps-field__hint">{t.importStockHelp}</p>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          setOpen(false);
        }}
      >
        {t.importColumnsHide}
      </Button>
    </div>
  );
}

function ImportResult({
  run,
  applying,
  onApply,
}: {
  run: ImportRunResponse;
  applying: boolean;
  onApply: () => void;
}): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const applied = run.status === 'APPLIED';

  const errorColumns: DataTableColumn<ImportRunResponse['errors'][number]>[] = [
    { key: 'row', header: t.importRow, numeric: true, render: (row) => String(row.row) },
    { key: 'column', header: t.importColumn, render: (row) => row.column ?? '—' },
    { key: 'value', header: t.importValue, render: (row) => row.value ?? '—' },
    {
      key: 'message',
      header: t.errorTitle,
      render: (row) => (locale === 'RU' ? row.messageRu : row.messageUz),
    },
  ];

  const previewColumns: DataTableColumn<ImportRunResponse['preview'][number]>[] = [
    { key: 'row', header: t.importRow, numeric: true, render: (row) => String(row.row) },
    { key: 'sku', header: t.productSku, render: (row) => row.sku },
    {
      key: 'action',
      header: t.importAction,
      render: (row) => (
        <span className={`adm-badge adm-badge--${row.isNew ? 'draft' : 'active'}`}>
          {row.isNew ? t.importWillCreate : t.importWillUpdate}
        </span>
      ),
    },
    { key: 'name', header: t.productName, render: (row) => row.nameUz ?? '—' },
    {
      key: 'price',
      header: t.productPrice,
      numeric: true,
      render: (row) => (row.price === null ? '—' : formatMoney(row.price, locale)),
    },
  ];

  return (
    <>
      <section className="adm-panel">
        <h2 className="adm-panel__title">{run.fileName}</h2>
        <div className="adm-panel__body">
          <Summary label={t.importTotal} value={run.totalRows} />
          <Summary label={t.importWillCreate} value={run.createRows} />
          <Summary label={t.importWillUpdate} value={run.updateRows} />
          <Summary label={t.importErrors} value={run.errorRows} />

          <div className="adm-kv">
            <span className="adm-kv__key">{t.importRecognized}</span>
            <span className="adm-kv__value">{run.columns.join(', ') || '—'}</span>
          </div>
          {run.stockShops.length > 0 && (
            <div className="adm-kv">
              <span className="adm-kv__key">{t.importStockShops}</span>
              <span className="adm-kv__value">{run.stockShops.join(', ')}</span>
            </div>
          )}
          {run.ignoredColumns.length > 0 && (
            <div className="adm-kv">
              {/* Tanilmagan sarlavha jimgina tashlanmaydi — u yerda
                  kerakli ma'lumot bo'lishi mumkin. */}
              <span className="adm-kv__key">{t.importIgnored}</span>
              <span className="adm-kv__value">{run.ignoredColumns.join(', ')}</span>
            </div>
          )}

          {applied ? (
            <p className="aps-muted">{t.importApplied}</p>
          ) : (
            <div className="adm-form__actions">
              <Button
                disabled={applying || run.createRows + run.updateRows === 0}
                onClick={onApply}
              >
                {applying ? t.statusApplying : t.importApply}
              </Button>
              {run.errorRows > 0 && (
                <span className="aps-muted">{t.importSkipHint(run.errorRows)}</span>
              )}
            </div>
          )}
        </div>
      </section>

      {run.errors.length > 0 && (
        <section className="adm-panel">
          <h2 className="adm-panel__title">{t.importErrors}</h2>
          <div className="adm-panel__body">
            <DataTable
              caption={t.importErrors}
              columns={errorColumns}
              rows={run.errors}
              rowKey={(row) => `${String(row.row)}-${row.column ?? ''}`}
            />
          </div>
        </section>
      )}

      {run.preview.length > 0 && !applied && (
        <section className="adm-panel">
          <h2 className="adm-panel__title">{t.importPreview}</h2>
          <div className="adm-panel__body">
            <DataTable
              caption={t.importPreview}
              columns={previewColumns}
              rows={run.preview}
              rowKey={(row) => row.sku}
            />
          </div>
        </section>
      )}
    </>
  );
}

function Summary({ label, value }: { label: string; value: number }): ReactNode {
  return (
    <div className="adm-kv">
      <span className="adm-kv__key">{label}</span>
      <span className="adm-kv__value">{String(value)}</span>
    </div>
  );
}
